<script>
  import { onDestroy } from 'svelte';
  import { link } from 'svelte-spa-router';
  import InfoHint from './InfoHint.svelte';
  import ErrorBanner from './ErrorBanner.svelte';
  import SuccessBanner from './SuccessBanner.svelte';
  import { companyInvoicesApi } from '../stores/api.js';
  import { capabilities } from '../stores/capabilities.js';
  import { isEmptyOrValidEmail } from '../lib/email-address.js';
  import { formatDateTime } from '../lib/labels.js';
  import { t, tImmediate } from '../i18n/index.js';
  import { Mail } from '../lib/icons/index.js';

  // "Copia di cortesia via email" on the invoice detail: every send of this
  // invoice, why there may be none, and the button to send one now. The server
  // decides everything shown here (GET …/courtesy-email); this only explains it.
  export let invoice;
  export let companyId;

  const STATUS_CLASS = {
    pending: 'badge-yellow',
    sending: 'badge-yellow',
    sent: 'badge-green',
    failed: 'badge-red',
    skipped: 'badge-gray',
    uncertain: 'badge-magenta',
  };
  const POLL_MS = 3_000;
  const MAX_POLLS = 40;

  let data = null;
  let error = '';
  let success = '';
  let override = '';
  let sending = false;
  let pollTimer = null;
  let polls = 0;
  let loadedKey = null;

  $: key = invoice?.id && companyId ? `${companyId}:${invoice.id}` : null;
  $: if (key !== loadedKey) start(key);

  function start(nextKey) {
    loadedKey = nextKey;
    clearTimeout(pollTimer);
    data = null;
    error = '';
    success = '';
    override = '';
    polls = 0;
    if (nextKey) load(nextKey);
  }

  async function load(forKey) {
    try {
      const value = await companyInvoicesApi.getCourtesyEmails(companyId, invoice.id);
      if (forKey !== loadedKey) return;
      data = value;
      schedulePoll(forKey);
    } catch (err) {
      if (forKey === loadedKey) error = err.message;
    }
  }

  // A queued send settles within seconds; the list follows it on its own.
  function schedulePoll(forKey) {
    clearTimeout(pollTimer);
    const active = data?.sends?.some((s) => s.status === 'pending' || s.status === 'sending');
    if (!active || polls >= MAX_POLLS) return;
    polls += 1;
    pollTimer = setTimeout(() => load(forKey), POLL_MS);
  }

  onDestroy(() => clearTimeout(pollTimer));

  $: canSend = $capabilities?.canSendCourtesyEmail === true;
  $: overrideInvalid = !isEmptyOrValidEmail(override);
  $: ready = data?.eligible && data?.smtp === 'ready';
  $: hasRecipient = Boolean(data?.defaultRecipient || override.trim());

  async function send() {
    error = '';
    success = '';
    if (overrideInvalid) {
      error = $t('invoices.courtesyEmail.overrideInvalid');
      return;
    }
    const forKey = loadedKey;
    sending = true;
    try {
      await companyInvoicesApi.sendCourtesyEmail(companyId, invoice.id, override.trim() || null);
      if (forKey !== loadedKey) return;
      success = $t('invoices.courtesyEmail.queued');
      override = '';
      polls = 0;
      await load(forKey);
    } catch (err) {
      if (forKey === loadedKey) error = err.message;
    } finally {
      sending = false;
    }
  }

  function detailOf(send) {
    const parts = [];
    if (send.status === 'skipped' && send.skipReason) {
      parts.push(tImmediate(`invoices.courtesyEmail.skipReason.${send.skipReason}`, undefined, send.skipReason));
    }
    if (send.lastErrorCategory && send.status !== 'sent') {
      let text = tImmediate(`email.categories.${send.lastErrorCategory}`, undefined, tImmediate('email.categories.unknown'));
      if (send.lastSmtpCode) text += ` (${tImmediate('email.smtpCode', { code: send.lastSmtpCode })})`;
      parts.push(text);
    }
    if (send.status === 'pending' && send.nextAttemptAt) {
      parts.push(tImmediate('invoices.courtesyEmail.nextAttempt', { when: formatDateTime(send.nextAttemptAt) }));
    }
    if (send.attempts > 1) parts.push(tImmediate('invoices.courtesyEmail.attempts', { count: send.attempts }));
    if (send.anonymizedAt) parts.push(tImmediate('invoices.courtesyEmail.anonymized'));
    return parts.join(' · ');
  }
</script>

<div class="mt-6 pt-5 border-t" style="border-color: var(--color-tint-medium);" id="courtesy-email-panel">
  <h4 class="eyebrow text-muted mb-1 inline-flex items-center gap-1">
    <Mail size={12} /> {$t('invoices.courtesyEmail.title')}
  </h4>
  <p class="text-xs text-muted mb-4 measure">{$t('invoices.courtesyEmail.description')}</p>

  <ErrorBanner {error} />
  <SuccessBanner message={success} />

  {#if data}
    {#if !data.eligible}
      <p class="text-xs mb-4 measure" style="color: var(--color-text-body);" id="courtesy-email-not-eligible">
        {tImmediate(`invoices.courtesyEmail.notEligible.${data.reason}`, undefined, data.reason)}
      </p>
    {:else if data.smtp !== 'ready'}
      <p class="text-xs mb-4 measure" style="color: var(--color-text-body);" id="courtesy-email-smtp">
        {$t(`invoices.courtesyEmail.smtp.${data.smtp}`)}
        {#if $capabilities?.canManageSmtp && data.smtp !== 'feature_unavailable'}
          <a href="/settings/email" use:link class="text-action underline ml-1">{$t('invoices.courtesyEmail.smtp.goToSettings')}</a>
        {/if}
      </p>
    {:else if canSend}
      <div class="mb-4">
        {#if data.defaultRecipient}
          <p class="text-xs mb-2" style="color: var(--color-text-body);">
            {$t('invoices.courtesyEmail.recipientDefault', { email: data.defaultRecipient })}
          </p>
        {:else}
          <p class="text-xs mb-2 measure text-warning">{$t('invoices.courtesyEmail.recipientMissing')}</p>
        {/if}
        <div class="flex flex-col sm:flex-row gap-2 sm:items-end">
          <label class="flex flex-col flex-1 max-w-sm">
            <span class="eyebrow text-muted mb-1">{$t('invoices.courtesyEmail.override')}</span>
            <input id="courtesy-email-override" class="input" type="email" maxlength="254" bind:value={override}
              placeholder={$t('invoices.courtesyEmail.overridePlaceholder')} aria-invalid={overrideInvalid} />
          </label>
          <button id="courtesy-email-send" class="btn btn-secondary btn-sm" on:click={send}
            disabled={sending || !ready || !hasRecipient || overrideInvalid}>
            <Mail size={14} class="mr-1 {sending ? 'animate-pulse' : ''}" />
            {sending ? $t('invoices.courtesyEmail.sending')
              : data.sends.some((s) => s.status === 'sent' || s.status === 'uncertain')
                ? $t('invoices.courtesyEmail.resend') : $t('invoices.courtesyEmail.send')}
          </button>
        </div>
        {#if overrideInvalid}
          <p class="mt-1 text-xs text-warning" role="status">{$t('invoices.courtesyEmail.overrideInvalid')}</p>
        {/if}
      </div>
    {:else if $capabilities}
      <p class="text-xs text-muted mb-4 measure">{$t('invoices.courtesyEmail.readOnly')}</p>
    {/if}

    {#if data.sends.length === 0}
      <p class="text-xs text-muted" id="courtesy-email-empty">{$t('invoices.courtesyEmail.empty')}</p>
    {:else}
      <div class="overflow-x-auto">
        <table class="table-modern" id="courtesy-email-sends">
          <thead class="table-header">
            <tr>
              <th class="table-cell text-left eyebrow text-muted">{$t('invoices.courtesyEmail.columns.when')}</th>
              <th class="table-cell text-left eyebrow text-muted">{$t('invoices.courtesyEmail.columns.trigger')}</th>
              <th class="table-cell text-left eyebrow text-muted">{$t('invoices.courtesyEmail.columns.recipient')}</th>
              <th class="table-cell text-left eyebrow text-muted">{$t('invoices.courtesyEmail.columns.status')}</th>
              <th class="table-cell text-left eyebrow text-muted">{$t('invoices.courtesyEmail.columns.detail')}</th>
            </tr>
          </thead>
          <tbody>
            {#each data.sends as s (s.id)}
              <tr class="table-row" style="cursor: default;">
                <td class="table-cell text-xs font-mono whitespace-nowrap">{formatDateTime(s.sentAt || s.createdAt)}</td>
                <td class="table-cell text-xs">{$t(`invoices.courtesyEmail.trigger.${s.trigger}`)}</td>
                <td class="table-cell text-xs font-mono break-all">{s.recipient || '—'}</td>
                <td class="table-cell text-xs whitespace-nowrap">
                  <span class="badge {STATUS_CLASS[s.status] || 'badge-gray'}"
                    title={tImmediate(`invoices.courtesyEmail.statusHelp.${s.status}`, undefined, '')}>
                    {$t(`invoices.courtesyEmail.status.${s.status}`)}
                  </span>
                  <InfoHint text={tImmediate(`invoices.courtesyEmail.statusHelp.${s.status}`, undefined, '')} />
                </td>
                <td class="table-cell text-xs measure">{detailOf(s)}</td>
              </tr>
            {/each}
          </tbody>
        </table>
      </div>
    {/if}
  {/if}
</div>
